import React from 'react';
import apiClient from '../../apiClient';
import { withRouter } from 'react-router-dom';
import StorageIcon from '@material-ui/icons/Storage';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import PeopleIcon from '@material-ui/icons/People';
import StoreIcon from '@material-ui/icons/Store';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import WarningIcon from '@material-ui/icons/Warning';
import WorkIcon from '@material-ui/icons/Work';
import ReceiptIcon from '@material-ui/icons/Receipt';
import InputIcon from '@material-ui/icons/Input';
import DashboardIcon from '@material-ui/icons/Dashboard';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import Button from '@material-ui/core/Button';
import RefreshIcon from '@material-ui/icons/Refresh';
import Spinner from '../Spinner';
import './UserDashboard.scss';

const currency = (value) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(value) || 0
  );

const qty = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 2 }) : '0';
};

class UserDashboard extends React.Component {
  state = {
    loading: true,
    errorMessage: null,
    data: null,
  };

  componentDidMount() {
    this.loadDashboard();
    this.onWindowFocus = () => this.loadDashboard();
    window.addEventListener('focus', this.onWindowFocus);
  }

  componentWillUnmount() {
    window.removeEventListener('focus', this.onWindowFocus);
  }

  loadDashboard = () => {
    const user = JSON.parse(sessionStorage.getItem('user') || 'null');
    if (!user || !user.isLoggedIn) {
      const { history } = this.props;
      history.push('/');
      return;
    }

    const accessToken = user.accessToken || '';
    this.setState({ loading: true, errorMessage: null });

    apiClient
      .get('/api/get/user-dashboard', {
        params: { userId: user.userId, lowStockThreshold: 10 },
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((res) => {
        this.setState({ data: res.data, loading: false });
      })
      .catch(() => {
        this.setState({
          loading: false,
          errorMessage: 'Unable to load dashboard. Please try again.',
        });
      });
  };

  renderSection = (title, children) => (
    <section className="user-dashboard__section">
      <h2 className="user-dashboard__section-title">{title}</h2>
      {children}
    </section>
  );

  renderMetricCard = (label, value, Icon, variant = '') => (
    <div className={`user-dashboard__metric ${variant}`}>
      <div className="user-dashboard__metric-icon-wrap">
        {Icon && <Icon className="user-dashboard__metric-icon" />}
      </div>
      <div className="user-dashboard__metric-body">
        <span className="user-dashboard__metric-label">{label}</span>
        <span className="user-dashboard__metric-value">{value}</span>
      </div>
    </div>
  );

  renderDataTable = (columns, rows, emptyMessage) => (
    <div className="user-dashboard__table-wrap">
      <table className="user-dashboard__table">
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.align ? `is-${col.align}` : ''}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="user-dashboard__table-empty">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row) => (
              <tr key={row.key}>
                {columns.map((col) => (
                  <td key={col.key} className={col.align ? `is-${col.align}` : ''}>
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  render() {
    const { loading, errorMessage, data } = this.state;
    const metrics = (data && data.metrics) || {};
    const userActivity = (data && data.userActivity) || {};
    const lowStockAlerts = (data && data.lowStockAlerts) || [];
    const vendorCommissions = (data && data.vendorCommissions) || [];
    const recentActivity = (data && data.recentActivity) || [];
    const userInfo = (data && data.user) || {};
    const periodDays = userActivity.periodDays || 30;

    const lowStockRows = lowStockAlerts.map((item) => ({
      key: item.itemId,
      itemName: item.itemName,
      itemNumber: item.itemNumber,
      metricUID: item.metricUID || '—',
      quantity: `${qty(item.quantity)} left`,
    }));

    const vendorRows = vendorCommissions.map((v) => ({
      key: v.vendorId,
      vendorName: v.vendorName,
      products: qty(v.productCount),
      units: qty(v.totalUnits),
      margin: `${Number(v.marginRate).toFixed(1)}%`,
      commission: currency(v.commissionAmount),
    }));

    const recentRows = recentActivity.map((row) => ({
      key: `${row.ticketNumber}-${row.transactionType}-${row.date}`,
      ticketNumber: row.ticketNumber,
      type: row.transactionType,
      date: row.date,
      detail: row.detail || '—',
      amount:
        row.transactionType === 'CheckOut'
          ? currency(row.ticketTotal)
          : row.transactionType === 'Item created'
            ? '—'
            : qty(row.ticketTotal),
    }));

    return (
      <div className="user-dashboard">
        <div className="user-dashboard__inner">
          <header className="user-dashboard__header">
            <div>
              <h1 className="user-dashboard__title">My Dashboard</h1>
              <p className="user-dashboard__subtitle">
                Operational and sales summary for your account
              </p>
            </div>
            <div className="user-dashboard__header-actions">
              {userInfo.userName && (
                <div className="user-dashboard__user-chip">
                  <span className="user-dashboard__user-name">{userInfo.userName}</span>
                  {userInfo.userRole && (
                    <span className="user-dashboard__user-role">{userInfo.userRole}</span>
                  )}
                </div>
              )}
              <Button
                variant="outlined"
                color="primary"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={this.loadDashboard}
                disabled={loading}
              >
                Refresh
              </Button>
            </div>
          </header>

          {errorMessage && (
            <div className="user-dashboard__error">{errorMessage}</div>
          )}

          {loading ? (
            <Spinner />
          ) : (
            data && (
              <div className="user-dashboard__body">
                {this.renderSection(
                  'Inventory overview',
                  <>
                    <div className="user-dashboard__metrics-row">
                      {this.renderMetricCard('Products', qty(metrics.productCount), StorageIcon)}
                      {this.renderMetricCard('Total units', qty(metrics.totalUnits), TrendingUpIcon, 'is-accent')}
                      {this.renderMetricCard('Clients', qty(metrics.clientCount), PeopleIcon)}
                      {this.renderMetricCard('Vendors', qty(metrics.vendorCount), StoreIcon, 'is-accent')}
                    </div>
                    <div className="user-dashboard__finance-row">
                      <div className="user-dashboard__finance-card">
                        <span className="user-dashboard__finance-label">Inventory value (cost)</span>
                        <span className="user-dashboard__finance-value">
                          {currency(metrics.inventoryCostValue)}
                        </span>
                      </div>
                      <div className="user-dashboard__finance-card is-highlight">
                        <span className="user-dashboard__finance-label">Potential sale value</span>
                        <span className="user-dashboard__finance-value">
                          {currency(metrics.potentialSaleValue)}
                        </span>
                      </div>
                    </div>
                  </>
                )}

                {this.renderSection(
                  `Your activity (last ${periodDays} days)`,
                  <div className="user-dashboard__metrics-row user-dashboard__metrics-row--activity">
                    {this.renderMetricCard('Invoices', qty(userActivity.invoiceCount), ReceiptIcon)}
                    {this.renderMetricCard('Purchase orders', qty(userActivity.purchaseOrderCount), InputIcon)}
                    {this.renderMetricCard('Reserves', qty(userActivity.reserveCount), DashboardIcon)}
                    {this.renderMetricCard('Returns', qty(userActivity.returnCount), WarningIcon)}
                    {this.renderMetricCard('Items added', qty(userActivity.itemsAdded), AddCircleOutlineIcon, 'is-accent')}
                    {this.renderMetricCard(
                      'Sales total',
                      currency(userActivity.salesTotal),
                      AttachMoneyIcon,
                      'is-accent'
                    )}
                  </div>
                )}

                <div className="user-dashboard__split">
                  <div className="user-dashboard__panel">
                    <div className="user-dashboard__panel-head">
                      <div className="user-dashboard__panel-title">
                        <WarningIcon className="user-dashboard__panel-icon is-warn" />
                        Low stock alerts
                      </div>
                      {lowStockAlerts.length > 0 && (
                        <span className="user-dashboard__badge">{lowStockAlerts.length}</span>
                      )}
                    </div>
                    {this.renderDataTable(
                      [
                        { key: 'itemName', label: 'Item name' },
                        { key: 'itemNumber', label: 'Item #' },
                        { key: 'metricUID', label: 'MetrcUID' },
                        { key: 'quantity', label: 'On hand', align: 'right' },
                      ],
                      lowStockRows,
                      'No low stock items'
                    )}
                  </div>

                  <div className="user-dashboard__panel">
                    <div className="user-dashboard__panel-head">
                      <div className="user-dashboard__panel-title">
                        <WorkIcon className="user-dashboard__panel-icon is-accent" />
                        Vendor summary
                      </div>
                    </div>
                    {this.renderDataTable(
                      [
                        { key: 'vendorName', label: 'Vendor' },
                        { key: 'products', label: 'Products', align: 'right' },
                        { key: 'units', label: 'Units', align: 'right' },
                        { key: 'margin', label: 'Margin', align: 'right' },
                        { key: 'commission', label: 'Est. commission', align: 'right' },
                      ],
                      vendorRows,
                      'No vendor data'
                    )}
                  </div>
                </div>

                <div className="user-dashboard__panel user-dashboard__panel--full">
                  <div className="user-dashboard__panel-head">
                    <div className="user-dashboard__panel-title">
                      <ReceiptIcon className="user-dashboard__panel-icon" />
                      Your recent tickets
                    </div>
                    <span className="user-dashboard__panel-meta">
                      {qty(userActivity.ticketsCreated)} tickets created in the last {periodDays} days
                    </span>
                  </div>
                  {this.renderDataTable(
                    [
                      { key: 'ticketNumber', label: 'Ticket / ref' },
                      { key: 'type', label: 'Type' },
                      { key: 'date', label: 'Date' },
                      { key: 'detail', label: 'Details' },
                      { key: 'amount', label: 'Amount', align: 'right' },
                    ],
                    recentRows,
                    'No recent activity for your account'
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(UserDashboard);
